import React, { useState, useRef, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const StrategyCanvas = ({ 
  components, 
  connections, 
  selectedComponent, 
  onComponentSelect, 
  onComponentMove, 
  onComponentDelete, 
  onConnectionCreate, 
  onConnectionDelete,
  onDrop 
}) => {
  const canvasRef = useRef(null);
  const [draggedComponent, setDraggedComponent] = useState(null);
  const [connectionStart, setConnectionStart] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [canvasOffset, setCanvasOffset] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);

  const handleDragOver = (e) => {
    e?.preventDefault();
  };

  const handleDrop = useCallback((e) => {
    e?.preventDefault();
    const rect = canvasRef?.current?.getBoundingClientRect();
    const x = (e?.clientX - rect?.left - canvasOffset?.x) / zoom;
    const y = (e?.clientY - rect?.top - canvasOffset?.y) / zoom;
    
    try {
      const componentData = JSON.parse(e?.dataTransfer?.getData('application/json'));
      onDrop(componentData, { x, y });
    } catch (error) {
      console.error('Error parsing dropped component:', error);
    }
  }, [onDrop, canvasOffset, zoom]);

  const handleComponentDragStart = (e, component) => {
    setDraggedComponent(component);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleComponentDrag = (e, componentId) => {
    if (e?.clientX === 0 && e?.clientY === 0) return; // Ignore drag end event
    
    const rect = canvasRef?.current?.getBoundingClientRect();
    const x = (e?.clientX - rect?.left - canvasOffset?.x) / zoom;
    const y = (e?.clientY - rect?.top - canvasOffset?.y) / zoom;
    
    onComponentMove(componentId, { x, y });
  };

  const handleConnectionStart = (componentId, outputIndex) => {
    setConnectionStart({ componentId, outputIndex });
    setIsConnecting(true);
  };

  const handleConnectionEnd = (componentId, inputIndex) => {
    if (connectionStart && connectionStart?.componentId !== componentId) {
      onConnectionCreate({
        from: connectionStart?.componentId,
        fromOutput: connectionStart?.outputIndex,
        to: componentId,
        toInput: inputIndex
      });
    }
    setConnectionStart(null);
    setIsConnecting(false);
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev * 1.2, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev / 1.2, 0.3));
  };

  const handleResetView = () => {
    setZoom(1);
    setCanvasOffset({ x: 0, y: 0 });
  };

  const renderConnection = (connection) => {
    const fromComponent = components?.find(c => c?.instanceId === connection?.from);
    const toComponent = components?.find(c => c?.instanceId === connection?.to);
    
    if (!fromComponent || !toComponent) return null;

    const fromX = fromComponent?.position?.x + 200; // Component width
    const fromY = fromComponent?.position?.y + 30 + (connection?.fromOutput * 20);
    const toX = toComponent?.position?.x;
    const toY = toComponent?.position?.y + 30 + (connection?.toInput * 20);

    const midX = (fromX + toX) / 2;
    
    return (
      <g key={`${connection?.from}-${connection?.to}-${connection?.fromOutput}-${connection?.toInput}`}>
        <path
          d={`M ${fromX} ${fromY} C ${midX} ${fromY} ${midX} ${toY} ${toX} ${toY}`}
          stroke="var(--color-primary)"
          strokeWidth="2"
          fill="none"
          className="hover:stroke-accent cursor-pointer"
          onClick={() => onConnectionDelete(connection)}
        />
        <circle
          cx={fromX}
          cy={fromY}
          r="4"
          fill="var(--color-primary)"
        />
        <circle
          cx={toX}
          cy={toY}
          r="4"
          fill="var(--color-accent)"
        />
      </g>
    );
  };

  const renderComponent = (component) => {
    const isSelected = selectedComponent?.instanceId === component?.instanceId;
    
    return (
      <div
        key={component?.instanceId}
        className={`absolute bg-card border-2 rounded-lg shadow-md cursor-move select-none transition-all duration-200 ${
          isSelected ? 'border-primary shadow-lg' : 'border-border hover:border-primary/50'
        }`}
        style={{
          left: component?.position?.x,
          top: component?.position?.y,
          width: '200px',
          minHeight: '80px'
        }}
        draggable
        onDragStart={(e) => handleComponentDragStart(e, component)}
        onDrag={(e) => handleComponentDrag(e, component?.instanceId)}
        onClick={() => onComponentSelect(component)}
      >
        {/* Component Header */}
        <div className={`flex items-center gap-2 p-3 ${component?.color} rounded-t-lg`}>
          <Icon name={component?.icon} size={16} color="white" />
          <span className="text-white font-medium text-sm flex-1 truncate">{component?.name}</span>
          <Button
            variant="ghost"
            size="xs"
            onClick={(e) => {
              e?.stopPropagation();
              onComponentDelete(component?.instanceId);
            }}
            className="text-white hover:bg-white/20 p-1"
          >
            <Icon name="X" size={12} />
          </Button>
        </div>
        {/* Component Body */}
        <div className="p-3">
          {/* Inputs */}
          {component?.inputs && component?.inputs?.length > 0 && (
            <div className="mb-2">
              <div className="text-xs font-medium text-muted-foreground mb-1">Inputs:</div>
              {component?.inputs?.map((input, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 text-xs text-foreground mb-1 relative"
                >
                  <div
                    className="w-3 h-3 bg-accent rounded-full cursor-pointer hover:bg-accent/80 absolute -left-6"
                    onClick={() => handleConnectionEnd(component?.instanceId, index)}
                  />
                  <span className="ml-2">{input}</span>
                </div>
              ))}
            </div>
          )}

          {/* Outputs */}
          {component?.outputs && component?.outputs?.length > 0 && (
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-1">Outputs:</div>
              {component?.outputs?.map((output, index) => (
                <div
                  key={index}
                  className="flex items-center justify-end gap-2 text-xs text-foreground mb-1 relative"
                >
                  <span className="mr-2">{output}</span>
                  <div
                    className="w-3 h-3 bg-primary rounded-full cursor-pointer hover:bg-primary/80 absolute -right-6"
                    onClick={() => handleConnectionStart(component?.instanceId, index)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Status Indicator */}
        <div className="absolute top-2 right-2">
          <div className={`w-2 h-2 rounded-full ${
            component?.status === 'active' ? 'bg-success' :
            component?.status === 'error'? 'bg-error' : 'bg-muted-foreground'
          }`} />
        </div>
      </div>
    );
  };

  return (
    <div className="flex-1 relative bg-muted/30 overflow-hidden">
      {/* Canvas Controls */}
      <div className="absolute top-4 right-4 z-10 flex gap-2">
        <Button variant="outline" size="sm" onClick={handleZoomOut}>
          <Icon name="ZoomOut" size={16} />
        </Button>
        <Button variant="outline" size="sm" onClick={handleResetView}>
          <Icon name="RotateCcw" size={16} />
        </Button>
        <Button variant="outline" size="sm" onClick={handleZoomIn}>
          <Icon name="ZoomIn" size={16} />
        </Button>
        <div className="bg-card border border-border rounded px-2 py-1 text-xs font-medium">
          {Math.round(zoom * 100)}%
        </div>
      </div>
      {/* Canvas */}
      <div
        ref={canvasRef}
        className="w-full h-full relative"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        style={{
          transform: `scale(${zoom}) translate(${canvasOffset?.x}px, ${canvasOffset?.y}px)`,
          transformOrigin: '0 0'
        }}
      >
        {/* Grid Background */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `
              linear-gradient(to right, var(--color-border) 1px, transparent 1px),
              linear-gradient(to bottom, var(--color-border) 1px, transparent 1px)
            `,
            backgroundSize: '20px 20px'
          }}
        />

        {/* SVG for Connections */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 1 }}>
          {connections?.map(renderConnection)}
        </svg>

        {/* Components */}
        <div className="relative" style={{ zIndex: 2 }}>
          {components?.map(renderComponent)}
        </div>

        {/* Empty State */}
        {components?.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <Icon name="Workflow" size={48} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">Start Building Your Strategy</h3>
              <p className="text-muted-foreground mb-4 max-w-md">
                Drag components from the library to create your trading strategy. Connect components to define data flow and logic.
              </p>
              <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Icon name="MousePointer2" size={16} />
                  <span>Drag & Drop</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="Link" size={16} />
                  <span>Connect Components</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="Settings" size={16} />
                  <span>Configure Parameters</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Connection Mode Indicator */}
      {isConnecting && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-primary text-primary-foreground px-4 py-2 rounded-md text-sm font-medium">
          Click on an input to complete the connection
        </div>
      )}
    </div>
  );
};

export default StrategyCanvas;